/**
 * @see docs/lightbox-contract.md (if created) for full behavioral spec
 */

export type LightboxMediaType = "image" | "video";

/**
 * A single slide in a lightbox gallery.
 * Built from anchor attributes at click time.
 */
export interface LightboxSlide {
  /** Full media URL (from href) */
  src: string;
  /** Optional caption shown below media */
  caption?: string;
  /** Detected media type for rendering */
  type: LightboxMediaType;
  /** Optional poster URL for video (thumbnail before play) */
  poster?: string;
  /** Thumbnail URL for strip (from child img or derived from src) */
  thumb?: string;
  /** Optional width hint for video (e.g. "100%") */
  width?: string;
  /** Optional height hint for video (e.g. "100%") */
  height?: string;
  /** DOM order index in the group (for next/prev) */
  index: number;
  /** When set, the lightbox crops the image to this min width/height ratio (e.g. 0.81 for Meta 4:5) */
  cropMinAspectRatio?: number;
}

/**
 * A group of slides share the same data-lightbox key.
 * Slide order MUST follow DOM order of anchors in the document.
 */
export interface LightboxGroup {
  /** Group key (exact value of data-lightbox) */
  key: string;
  /** Slides in DOM order */
  slides: LightboxSlide[];
  /** Index of the clicked slide (0-based) */
  activeIndex: number;
}

/**
 * Attributes read from anchors for compatibility.
 */
export interface LightboxAnchorAttrs {
  /** Group key - anchors with same value form one gallery */
  "data-lightbox": string;
  /** Optional caption (can be dynamic, e.g. "Image is selected") */
  "data-caption"?: string;
  /** Optional video size hint */
  "data-width"?: string;
  /** Optional video size hint */
  "data-height"?: string;
  /** When present, crop this image slide to the given min aspect ratio */
  "data-crop-min-aspect"?: string;
  /** Source URL */
  href: string;
}

/**
 * Selector for lightbox-trigger anchors.
 * Must match anchors that have data-lightbox.
 */
export const LIGHTBOX_TRIGGER_SELECTOR = "a[data-lightbox][href]";
